import { useState, useEffect, useMemo } from "react";
import { Tabs, Tab, ReactJson, Dropdown, Banner } from "../../components";
import { TabPanel, TabContext } from "@material-ui/lab";

import TaskSummary from "./TaskSummary";
import TaskLogs from "./TaskLogs";

import { makeStyles } from "@material-ui/styles";
import _ from "lodash";
import TaskPollData from "./TaskPollData";
import TaskDetailsTab from "./TaskDetailsTab";
import { useTabsList } from "../../contexts/TabsContext";

const useStyles = makeStyles({
  banner: {
    margin: 15,
  },
  dfSelect: {
    padding: 15,
    backgroundColor: "#efefef",
  },
  tabPanel: {
    padding: 0,
    flex: 1,
    overflowY: "auto",
  },
  errorMessage: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    textAlign: 'center',
    padding: '20px'
  },
});

export default function RightPanel({ selectedTask, dag, onTaskChange, view }) {
  const { tabsList } = useTabsList();

  const tasklevelaccess = useMemo(
    () =>
      tabsList?.Execution?.workflows?.tasklevelaccess || {
        summary: "True",
        Input: "True",
        Output: "True",
        logs: "True",
        JSON: "True",
        Definition: "True",
      },
    [tabsList]
  );

  const [tabIndex, setTabIndex] = useState(
    view === "TICKET_SUMMARY" ? "details" : "summary"
  );

  const classes = useStyles();

  useEffect(() => {
    setTabIndex(view === "TICKET_SUMMARY" ? "details" : "summary"); // Reset to Status Tab on ref change
  }, [selectedTask, view]);

  const taskResult = useMemo(
    () => dag && dag.resolveTaskResult(selectedTask),
    [dag, selectedTask]
  );
  const dfOptions = useMemo(
    () => dag && dag.getSiblings(selectedTask),
    [dag, selectedTask]
  );
  const retryOptions = useMemo(
    () => dag && dag.getRetries(selectedTask),
    [dag, selectedTask]
  );

  // build dynamic tabs list based on permissions & view
  const availableTabs = useMemo(
    () =>
      view === "TICKET_SUMMARY"
        ? [{ key: "details", label: "Details", disabled: false }]
        : [
            ...(tasklevelaccess.summary === "True"
              ? [{ key: "summary", label: "Summary", disabled: false }]
              : []),
            ...(tasklevelaccess.Input === "True"
              ? [
                  {
                    key: "input",
                    label: "Input",
                    disabled: !taskResult?.status,
                  },
                ]
              : []),
            ...(tasklevelaccess.Output === "True"
              ? [
                  {
                    key: "output",
                    label: "Output",
                    disabled: !taskResult?.status,
                  },
                ]
              : []),
            ...(tasklevelaccess.logs === "True"
              ? [{ key: "logs", label: "Logs", disabled: !taskResult?.status }]
              : []),
            ...(tasklevelaccess.JSON === "True"
              ? [{ key: "json", label: "JSON", disabled: !taskResult?.status }]
              : []),
            ...(tasklevelaccess.Definition === "True"
              ? [{ key: "definition", label: "Definition", disabled: false }]
              : []),
            ...(_.get(taskResult, "workflowTask.type") === "SIMPLE"
              ? [
                  {
                    key: "pollData",
                    label: "Poll Data",
                    disabled: !taskResult?.status,
                  },
                ]
              : []),
          ],
    [view, tasklevelaccess, taskResult]
  );

  // if current tabIndex isn't in availableTabs, reset to the first one
  useEffect(() => {
    if (
      availableTabs.length &&
      !availableTabs.some((t) => t.key === tabIndex)
    ) {
      setTabIndex(availableTabs[0].key);
    }
  }, [availableTabs, tabIndex]);

  if (!taskResult) {
    return (
      <div className={classes.errorMessage}>
        Failed to load task details please try again
      </div>
    );
  } else
    return (
      <TabContext value={tabIndex}>
        {dfOptions && (
          <div className={classes.dfSelect}>
            <Dropdown
              onChange={(e, v) => {
                onTaskChange({ ref: v.ref });
              }}
              options={dfOptions}
              disableClearable
              value={dfOptions.find(
                (opt) => opt.ref === taskResult.referenceTaskName
              )}
              getOptionLabel={(x) => `${dropdownIcon(x.status)} ${x.ref}`}
              style={{ marginBottom: 20, width: 500 }}
            />
          </div>
        )}

        {_.size(retryOptions) > 1 && (
          <div className={classes.dfSelect}>
            <Dropdown
              label="Retried Task - Select an instance"
              disableClearable
              onChange={(e, v) => {
                onTaskChange({
                  id: v.taskId,
                });
              }}
              options={retryOptions}
              value={retryOptions.find(
                (opt) => opt.taskId === taskResult.taskId
              )}
              getOptionLabel={(t) =>
                `${dropdownIcon(t.status)} Attempt ${t.retryCount} - ${t.taskId
                }`
              }
              style={{ marginBottom: 20, width: 500 }}
            />
          </div>
        )}

        <Tabs value={tabIndex} contextual onChange={(e, v) => setTabIndex(v)}>
          {availableTabs.map((tab) => (
            <Tab
              key={tab.key}
              label={tab.label}
              value={tab.key}
              disabled={tab.disabled}
            />
          ))}
        </Tabs>
        <>
          {availableTabs.some((t) => t.key === "details") && (
            <TabPanel className={classes.tabPanel} value="details">
              <TaskDetailsTab taskResult={taskResult} />
            </TabPanel>
          )}
          {availableTabs.some((t) => t.key === "summary") && (
            <TabPanel className={classes.tabPanel} value="summary">
              <TaskSummary taskResult={taskResult} />
            </TabPanel>
          )}
          {availableTabs.some((t) => t.key === "input") && (
            <TabPanel className={classes.tabPanel} value="input">
              {taskResult.externalInputPayloadStoragePath ? (
                <Banner className={classes.banner}>
                  This task has externalized input. Please reference{" "}
                  <code>externalInputPayloadStoragePath</code> for the storage
                  location.
                </Banner>
              ) : (
                <ReactJson src={taskResult.inputData} label="Task Input" />
              )}
            </TabPanel>
          )}
          {availableTabs.some((t) => t.key === "output") && (
            <TabPanel className={classes.tabPanel} value="output">
              {taskResult.externalOutputPayloadStoragePath ? (
                <Banner className={classes.banner}>
                  This task has externalized output. Please reference{" "}
                  <code>externalOutputPayloadStoragePath</code> for the storage
                  location.
                </Banner>
              ) : (
                <ReactJson src={taskResult.outputData} label="Task Output" />
              )}
            </TabPanel>
          )}
          {availableTabs.some((t) => t.key === "pollData") && (
            <TabPanel className={classes.tabPanel} value="pollData">
              <TaskPollData task={taskResult} />
            </TabPanel>
          )}
          {availableTabs.some((t) => t.key === "logs") && (
            <TabPanel className={classes.tabPanel} value="logs">
              <TaskLogs task={taskResult} />
            </TabPanel>
          )}
          {availableTabs.some((t) => t.key === "json") && (
            <TabPanel className={classes.tabPanel} value="json">
              <ReactJson
                src={taskResult}
                label="Unabridged Task Execution Result"
              />
            </TabPanel>
          )}
          {availableTabs.some((t) => t.key === "definition") && (
            <TabPanel className={classes.tabPanel} value="definition">
              <ReactJson
                src={taskResult.workflowTask}
                label="Task Definition at Runtime"
              />
            </TabPanel>
          )}
        </>
      </TabContext>
    );
}

function dropdownIcon(status) {
  let icon;
  switch (status) {
    case "COMPLETED":
      icon = "\u2705";
      break; // Green-checkmark
    case "COMPLETED_WITH_ERRORS":
      icon = "\u2757";
      break; // Exclamation
    case "CANCELED":
      icon = "\uD83D\uDED1";
      break; // stopsign
    case "IN_PROGRESS":
    case "SCHEDULED":
      icon = "\u231B";
      break; // hourglass
    default:
      icon = "\u274C"; // red-X
  }
  return icon + "\u2003";
}
